import { HTMLAttributes } from "react";
import { FormattedMessage } from "react-intl";
import { VisuallyHidden } from "radix-ui";
import classNames from "classnames/bind";
import { Button } from "app/components/ui/Button/Button";
import { ICON_SIZES } from "app/constants";
import {
	BaggageLDisabledFilledIcon,
	BaggageLFilledIcon,
	BaggageMDisabledFilledIcon,
	BaggageMFilledIcon,
	BaggageSDisabledFilledIcon,
	BaggageSFilledIcon,
	AirplaneFilledIcon,
} from "app/icons/travel";
import Typography from "app/pages/.shared/Typography/Typography";
import { getFlightNegotiatedLabelKey } from "app/utils/smartDPUtils";
import styles from "./FlightBaggageBlock.module.scss";

const cx = classNames.bind(styles);

type BaggageInclusion = { included?: boolean };

type Flight = {
	type?: string;
	outbound?: { legs?: unknown[] };
	inbound?: { legs?: unknown[] };
	flightOptions?: {
		flightBagageOption?: BaggageInclusion & {
			handBaggage?: BaggageInclusion;
			cabinBaggage?: BaggageInclusion;
		};
		flightBagageOptions?: { pricePerBooking?: number }[];
	};
};

export type FlightBaggageBlockProps = {
	/** Flight of the offer: drives the label and the baggage icons. */
	flight?: Flight;
	/** Opens the flight detail. Without it, the block is static and shows no detail button. */
	onViewDetail?: () => void;
} & HTMLAttributes<HTMLDivElement>;

/** Summary of the transport included in the offer, with its hand, cabin and checked baggage. */
export const FlightBaggageBlock = ({
	flight = {},
	onViewDetail,
	className,
	...rest
}: FlightBaggageBlockProps) => {
	const selectedBaggageOption = flight.flightOptions?.flightBagageOption;
	const firstBaggageOption = flight.flightOptions?.flightBagageOptions?.[0];

	const handBaggage = selectedBaggageOption?.handBaggage;
	const hasHandBaggageData = Object.keys(handBaggage ?? {}).length > 0;
	const isHandBaggageIncluded = Boolean(handBaggage?.included);

	const cabinBaggage = selectedBaggageOption?.cabinBaggage;
	const isCabinBaggageIncluded = Boolean(cabinBaggage?.included);

	const isCheckedBaggageIncluded = Boolean(selectedBaggageOption?.included);
	const isCheckedBaggageFree = firstBaggageOption?.pricePerBooking === 0;
	const hasCheckedBaggage = isCheckedBaggageIncluded || isCheckedBaggageFree;

	const handBaggageLabelId = isHandBaggageIncluded
		? "flight.baggage.block.hand.baggage.included"
		: "flight.baggage.block.hand.baggage.not.included";
	const cabinBaggageLabelId = isCabinBaggageIncluded
		? "flight.baggage.block.cabin.baggage.included"
		: "flight.baggage.block.cabin.baggage.not.included";

	const getCheckedBaggageLabelId = () => {
		if (isCheckedBaggageIncluded) {
			return "flight.baggage.block.checked.baggage.included";
		}

		if (isCheckedBaggageFree) {
			return "product.baggage.free.tag";
		}

		return "flight.baggage.block.checked.baggage.not.included";
	};

	const checkedBaggageLabelId = getCheckedBaggageLabelId();

	return (
		<div
			className={cx("flight-baggage-block", className)}
			data-testid="flight-baggage-block"
			{...rest}
		>
			<AirplaneFilledIcon
				size={ICON_SIZES.XS}
				aria-hidden="true"
				className={cx("flight-baggage-block__icon")}
			/>

			<Typography
				component="p"
				variant="regular"
				className={cx("flight-baggage-block__label")}
			>
				<FormattedMessage id={getFlightNegotiatedLabelKey(flight)} />

				{onViewDetail && (
					<Button
						type="button"
						variant="borderless"
						size="md"
						onClick={onViewDetail}
						className={cx("flight-baggage-block__detail-button")}
						data-testid="view-flight-detail"
					>
						<FormattedMessage id="sdp.listing.product.view.flight.button" />
					</Button>
				)}
			</Typography>

			<ul className={cx("flight-baggage-block__baggages")} data-testid="baggages">
				{hasHandBaggageData && (
					<li
						className={cx("flight-baggage-block__baggage")}
						data-included={isHandBaggageIncluded}
						data-testid="hand-baggage"
					>
						{isHandBaggageIncluded ? (
							<BaggageSFilledIcon size={ICON_SIZES.XS} aria-hidden="true" />
						) : (
							<BaggageSDisabledFilledIcon size={ICON_SIZES.XS} aria-hidden="true" />
						)}

						<VisuallyHidden.Root>
							<FormattedMessage id={handBaggageLabelId} />
						</VisuallyHidden.Root>
					</li>
				)}

				<li
					className={cx("flight-baggage-block__baggage")}
					data-included={isCabinBaggageIncluded}
					data-testid="cabin-baggage"
				>
					{isCabinBaggageIncluded ? (
						<BaggageMFilledIcon size={ICON_SIZES.XS} aria-hidden="true" />
					) : (
						<BaggageMDisabledFilledIcon size={ICON_SIZES.XS} aria-hidden="true" />
					)}

					<VisuallyHidden.Root>
						<FormattedMessage id={cabinBaggageLabelId} />
					</VisuallyHidden.Root>
				</li>

				<li
					className={cx("flight-baggage-block__baggage")}
					data-included={isCheckedBaggageIncluded}
					data-free={isCheckedBaggageFree}
					data-testid="checked-baggage"
				>
					{hasCheckedBaggage ? (
						<BaggageLFilledIcon size={ICON_SIZES.XS} aria-hidden="true" />
					) : (
						<BaggageLDisabledFilledIcon size={ICON_SIZES.XS} aria-hidden="true" />
					)}

					<VisuallyHidden.Root>
						<FormattedMessage id={checkedBaggageLabelId} />
					</VisuallyHidden.Root>
				</li>
			</ul>
		</div>
	);
};
